import React, { useState, useEffect } from 'react';
import { Table, InputNumber, Button, Tooltip, Badge, Space, Popconfirm, Tag, theme } from 'antd';
import { PlanRow, PlanBucket, PlanRowType } from '../types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pl';
import { PlusOutlined, DownOutlined, RightOutlined, DeleteOutlined } from '@ant-design/icons';

dayjs.extend(utc);
dayjs.locale('pl');

interface Props {
    rows: PlanRow[];
    onUpdateBucket: (bucketId: string, values: any) => void;
    onAddChannel: (parentId: string) => void;
    onAddSegment: (parentId: string) => void;
    onDeleteRow?: (rowId: string) => void;
    actualsData?: Record<string, { productId: string; channelId: string; month: string; revenue: number; units: number }[]>;
}

export default function PlanningTreeGrid({ rows, onUpdateBucket, onAddChannel, onAddSegment, onDeleteRow, actualsData }: Props) {
    const { token } = theme.useToken();
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [showActuals, setShowActuals] = useState(true);

    // Auto-expand all on load
    useEffect(() => {
        const keys: string[] = [];
        const traverse = (nodes: PlanRow[]) => {
            nodes.forEach(node => {
                keys.push(node.id);
                if (node.children) traverse(node.children);
            });
        };
        traverse(rows);
        setExpandedRowKeys(keys);
    }, [rows.length]);

    // Generate columns based on time buckets of the first row
    const sampleRow = rows[0] || (rows[0] as any)?.children?.[0];

    // Compute TOTAL row (sum of all SKU-level revenues per bucket, revenue only)
    const totalRow: PlanRow | null = sampleRow?.buckets?.length > 0 ? {
        id: 'total-row',
        rowType: 'TOTAL' as any,
        buckets: sampleRow.buckets.map((_: any, index: number) => {
            let totalPlanRevenue = 0;
            rows.forEach((row: PlanRow) => {
                const bucket = row.buckets?.[index];
                if (bucket) {
                    totalPlanRevenue += Number(bucket.revenue) || 0;
                }
            });
            return {
                id: `total-bucket-${index}`,
                bucketDate: sampleRow.buckets[index].bucketDate,
                revenue: totalPlanRevenue,
                units: 0,
                isLocked: false,
            };
        }),
        children: [],
    } as any : null;

    // Build columns for each month bucket
    const bucketCols = sampleRow?.buckets?.map((b, index) => {
        const monthKey = dayjs(b.bucketDate).format('YYYY-MM');

        return {
            title: () => (
                <div style={{ textAlign: 'center' }}>
                    {/* Add buffer to handle timezone shifts (e.g. Dec 31 23:00 -> Jan) */}
                    <div>{dayjs(b.bucketDate).add(2, 'day').format('MMM YYYY')}</div>
                    {b.isLocked && <Tag color="orange" style={{ fontSize: 10 }}>Zamknięty</Tag>}
                </div>
            ),
            children: [
                {
                    title: 'Plan',
                    width: 100,
                    key: `plan-${index}`,
                    render: (_: any, record: PlanRow) => {
                        const bucket = record.buckets[index];
                        if (!bucket) return <span style={{ color: '#ccc' }}>-</span>;

                        const isLeaf = !record.children || record.children.length === 0;
                        const isLocked = bucket.isLocked;
                        const isTotalRow = (record.rowType as string) === 'TOTAL' || record.id === 'total-row';

                        // TOTAL row is always read-only with special styling
                        if (isTotalRow) {
                            return (
                                <div style={{
                                    textAlign: 'right',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: '#1890ff',
                                    background: 'linear-gradient(90deg, rgba(24, 144, 255, 0.08) 0%, rgba(24, 144, 255, 0.02) 100%)',
                                    padding: '4px 8px',
                                    margin: '-4px -8px',
                                    borderRadius: 4,
                                }}>
                                    {Number(bucket.revenue).toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </div>
                            );
                        }

                        if (!isLeaf || isLocked) {
                            // Aggregated Read-only View
                            return (
                                <div style={{
                                    textAlign: 'right',
                                    fontWeight: isLeaf ? 400 : 'bold',
                                    color: isLocked ? '#999' : undefined
                                }}>
                                    {Number(bucket.revenue).toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </div>
                            );
                        }

                        // Editable Input
                        const asp = record.aspPln || (record.product ? (record.product as any).basePrice : 0) || 0;
                        const units = asp > 0 ? Math.ceil(bucket.revenue / asp) : 0;

                        return (
                            <Tooltip title={`${units} szt. (ASP: ${asp} PLN)`}>
                                <InputNumber
                                    value={bucket.revenue}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={(value: string | undefined) => {
                                        return (value!.replace(/\s?|(,*)/g, '')) as unknown as number;
                                    }}
                                    style={{ width: '100%', fontSize: 13, textAlign: 'right' }}
                                    variant="borderless"
                                    size="small"
                                    onBlur={(e) => {
                                        const inputValue = e.target.value.replace(/\s/g, '').replace(',', '.');
                                        // Treat empty or invalid input as 0
                                        const val = inputValue === '' ? 0 : parseFloat(inputValue);
                                        const finalVal = isNaN(val) ? 0 : val;
                                        if (finalVal !== bucket.revenue) {
                                            onUpdateBucket(bucket.id, { revenue: finalVal });
                                        }
                                    }}
                                    onPressEnter={(e) => {
                                        const target = e.target as HTMLInputElement;
                                        target.blur();
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    controls={false}
                                />
                            </Tooltip>
                        );
                    }
                },
                ...(showActuals ? [{
                    title: 'Actual',
                    width: 80,
                    key: `actual-${index}`,
                    render: (_: any, record: PlanRow) => {
                        const monthActuals = actualsData?.[monthKey] || [];
                        let actualRevenue = 0;
                        let actualUnits = 0;

                        // Function to filter matches based on row type
                        const getMatchingActuals = (r: PlanRow) => {
                            // TOTAL row matches ALL for the month
                            // We check strict rowType, or if id is total-row, or if it is a root summary row (often has undefined rowType in some table impls but here we expect TOTAL)
                            const isTotal = (r.rowType as string) === 'TOTAL' || r.id === 'total-row' || r.id === 'ROOT_TOTAL_FIXED';

                            if (isTotal) {
                                return monthActuals;
                            }

                            const pid = r.productId || r.product?.id;

                            // Debug logging for first few rows/months to avoid spam
                            if (index === 0 && Math.random() < 0.01) {
                                console.log('[PlanningTreeGrid] Matching:', {
                                    rowType: r.rowType,
                                    pid,
                                    actualsCount: monthActuals.length,
                                    rProductId: r.productId,
                                    rProductObj: r.product,
                                    sampleActual: monthActuals[0]
                                });
                            }

                            if (!pid) return [];

                            if (r.rowType === PlanRowType.SKU) {
                                return monthActuals.filter(a => String(a.productId) === String(pid));
                            } else if (r.rowType === PlanRowType.CHANNEL) {
                                return monthActuals.filter(a => String(a.productId) === String(pid) && String(a.channelId) === String(r.channelId));
                            }
                            // Segments usually don't have direct mapping in raw actuals unless mapped
                            return [];
                        };

                        const matches = getMatchingActuals(record);
                        actualRevenue = matches.reduce((sum, a) => sum + Number(a.revenue), 0);
                        actualUnits = matches.reduce((sum, a) => sum + Number(a.units), 0);

                        // If it's a leaf segment, we might need custom logic or just show 0 if no match
                        // But for SKU/Channel (parent) rows, this aggregation works.

                        const bucket = record.buckets[index];
                        const planRevenue = Number(bucket?.revenue) || 0;
                        const delta = actualRevenue - planRevenue;
                        const deltaPercent = planRevenue > 0 ? (actualRevenue / planRevenue) * 100 : 0;

                        // For TOTAL row to show values, we must allow it to render NOT just for leaf
                        const isTotalRow = (record.rowType as string) === 'TOTAL' || record.id === 'total-row' || record.id === 'ROOT_TOTAL_FIXED';

                        if (actualRevenue === 0 && !isTotalRow) {
                            return <span style={{ color: '#ccc', textAlign: 'right', display: 'block' }}>-</span>;
                        }

                        return (
                            <Tooltip title={`actual: ${actualRevenue.toLocaleString('pl-PL')} PLN, delta: ${delta >= 0 ? '+' : ''}${delta.toLocaleString('pl-PL')} PLN (${deltaPercent.toFixed(1)}%)`}>
                                <div style={{
                                    textAlign: 'right',
                                    color: delta >= 0 ? '#52c41a' : '#cf1322',
                                    fontSize: 12,
                                    fontWeight: (record.children && record.children.length > 0) || isTotalRow ? 'bold' : 'normal'
                                }}>
                                    {actualRevenue.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </div>
                            </Tooltip>
                        );
                    }
                }] : []),
                {
                    title: 'Szt.',
                    width: 60,
                    key: `units-${index}`,
                    render: (_: any, record: PlanRow) => {
                        const bucket = record.buckets[index];
                        if (!bucket) return null;

                        const asp = record.aspPln || (record.product ? (record.product as any).basePrice : 0) || 0;
                        const units = asp > 0 ? Math.ceil(Number(bucket.revenue) / asp) : Number(bucket.units) || 0;

                        return (
                            <div style={{ textAlign: 'right', color: '#666', fontSize: 11 }}>
                                {units > 0 ? units : '-'}
                            </div>
                        );
                    }
                }
            ]
        };
    }) || [];

    const columns = [
        {
            title: 'Hierarchia / SKU',
            key: 'name',
            width: 300,
            fixed: 'left' as const,
            render: (_: any, record: PlanRow) => {
                let icon = null;
                let label = '';
                let actions = null;

                // Special rendering for TOTAL row
                if ((record.rowType as any) === 'TOTAL') {
                    return (
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1890ff' }}>
                            TOTAL
                        </div>
                    );
                }

                if (record.rowType === PlanRowType.SKU) {
                    label = record.product ? `${record.product.name} (${record.product.sku})` : 'Unknown Product';
                    actions = (
                        <Space size={4}>
                            <Tooltip title="Dodaj kanał sprzedaży">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={(e) => { e.stopPropagation(); onAddChannel(record.id); }}
                                    style={{ color: '#1890ff' }}
                                />
                            </Tooltip>
                            {onDeleteRow && (
                                <Popconfirm
                                    title="Usunąć produkt z planu?"
                                    description="Zostaną usunięte wszystkie kanały i dane tego produktu."
                                    onConfirm={() => onDeleteRow(record.id)}
                                    okText="Usuń"
                                    cancelText="Anuluj"
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        style={{ color: '#ff4d4f' }}
                                    />
                                </Popconfirm>
                            )}
                        </Space>
                    );
                } else if (record.rowType === PlanRowType.CHANNEL) {
                    label = record.channel?.name || 'Channel';
                    const isB2B = record.channel?.name?.toUpperCase() === 'B2B';
                    actions = (
                        <Space size={4}>
                            {isB2B && (
                                <Tooltip title="Dodaj segment (np. Klient Kluczowy)">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        onClick={(e) => { e.stopPropagation(); onAddSegment(record.id); }}
                                        style={{ color: '#52c41a' }}
                                    />
                                </Tooltip>
                            )}
                            {onDeleteRow && (
                                <Popconfirm
                                    title="Usunąć kanał?"
                                    description="Dane sprzedaży dla tego kanału zostaną usunięte."
                                    onConfirm={() => onDeleteRow(record.id)}
                                    okText="Usuń"
                                    cancelText="Anuluj"
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        style={{ color: '#ff4d4f' }}
                                    />
                                </Popconfirm>
                            )}
                        </Space>
                    );
                } else if (record.rowType === PlanRowType.SEGMENT) {
                    label = record.segmentName || 'Segment';
                    if (onDeleteRow) {
                        actions = (
                            <Popconfirm
                                title="Usunąć segment?"
                                onConfirm={() => onDeleteRow(record.id)}
                                okText="Usuń"
                                cancelText="Anuluj"
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    style={{ color: '#ff4d4f' }}
                                />
                            </Popconfirm>
                        );
                    }
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                            {icon}
                            <span style={{ fontWeight: record.rowType === PlanRowType.SKU ? 600 : 400 }}>
                                {label}
                            </span>
                        </Space>
                        <div className="row-actions">
                            {actions}
                        </div>
                    </div>
                );
            }
        },
        ...bucketCols
    ];

    return (
        <div>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                    <Button
                        size="small"
                        type={showActuals ? 'primary' : 'default'}
                        onClick={() => setShowActuals(!showActuals)}
                    >
                        {showActuals ? 'Ukryj Actuals' : 'Pokaż Actuals'}
                    </Button>
                </Space>
            </div>
            <Table
                columns={columns}
                dataSource={totalRow ? [totalRow, ...rows] : rows}
                rowKey="id"
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                    expandIcon: ({ expanded, onExpand, record }) =>
                        record.children && record.children.length > 0 ? (
                            expanded ? (
                                <DownOutlined onClick={e => onExpand(record, e)} style={{ marginRight: 8 }} />
                            ) : (
                                <RightOutlined onClick={e => onExpand(record, e)} style={{ marginRight: 8 }} />
                            )
                        ) : <span style={{ marginRight: 22 }}></span>
                }}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content', y: 600 }}
                bordered
                rowClassName={(record) => {
                    if ((record.rowType as string) === 'TOTAL' || record.id === 'total-row') {
                        return 'total-summary-row';
                    }
                    return '';
                }}
                style={{}}
            />
            <style>{`
                .total-summary-row {
                    background: ${token.colorInfoBg} !important;
                    border-top: 2px solid ${token.colorPrimary} !important;
                    border-bottom: 2px solid ${token.colorPrimaryBorder} !important;
                }
                .total-summary-row td {
                    background: inherit !important;
                }
                .total-summary-row:hover td {
                    background: ${token.colorInfoBgHover} !important;
                }
            `}</style>
        </div>
    );
}
